import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  Shield, 
  Zap, 
  BarChart3, 
  Clock, 
  Target,
  ChevronRight,
  Check,
  X,
  Timer,
  Flame
} from "lucide-react";

const Landing = () => {
  const [showBotPromo, setShowBotPromo] = useState(false);
  const [promoCountdown, setPromoCountdown] = useState({ hours: 23, minutes: 59, seconds: 59 });

  // Show promo popup after 2s delay
  useEffect(() => {
    const promoSeen = sessionStorage.getItem('landing_bot_promo_seen');
    if (!promoSeen) {
      const timer = setTimeout(() => {
        setShowBotPromo(true);
        sessionStorage.setItem('landing_bot_promo_seen', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    const stored = localStorage.getItem('bot_promo_end');
    let endTime;
    if (stored) {
      endTime = parseInt(stored);
    } else {
      endTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('bot_promo_end', endTime.toString());
    }
    const tick = () => {
      const diff = Math.max(0, endTime - Date.now());
      setPromoCountdown({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000)
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Multi-Timeframe Analysis",
      description: "Deterministic signals using 1H/30M structure analysis with 15M and 5M confirmation."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "News Lockout Protection",
      description: "Automatic trade blocking during high-impact economic events."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Precise Entry/Exit",
      description: "Entry, Stop Loss, and 3 Take Profit levels calculated from market structure."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Real-Time Data",
      description: "Live market data with VWAP, RSI, and EMA regime confirmation."
    }
  ];

  const instruments = ["XAUUSD", "XAGUSD", "EURUSD", "AUDUSD", "USDJPY"];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="glass fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-8 h-8 text-white" />
            <span className="text-xl font-bold tracking-tight">Aurex AI</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn">
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold"
                data-testid="get-started-nav-btn"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1641355262431-021a396d4fe5?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-none">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-mono text-muted-foreground">LIVE SIGNALS ACTIVE</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-black leading-tight">
                DETERMINISTIC
                <br />
                <span className="text-muted-foreground">TRADE SIGNALS</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl">
                Rule-based strategy engine that generates precise BUY/SELL signals with Entry, Stop Loss, and 3 Take Profit levels. No guesswork, no emotions.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button 
                    size="lg"
                    className="bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold h-14 px-8"
                    data-testid="hero-get-started-btn"
                  >
                    Start Free Trial
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-none h-14 px-8 border-white/20"
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold font-mono" data-numeric>4</div>
                  <div className="text-sm text-muted-foreground">Free Trials</div>
                </div>
                <div className="w-px h-12 bg-border"></div>
                <div>
                  <div className="text-3xl font-bold font-mono" data-numeric>5</div>
                  <div className="text-sm text-muted-foreground">Instruments</div>
                </div>
                <div className="w-px h-12 bg-border"></div>
                <div>
                  <div className="text-3xl font-bold font-mono text-green-400" data-numeric>$306</div>
                  <div className="text-sm text-muted-foreground">/year <span className="text-green-400 text-xs">Save 15%</span></div>
                </div>
              </div>
            </div>

            {/* Signal Preview Card */}
            <div className="lg:col-span-5">
              <div className="bg-card border border-border p-6 space-y-6 animate-slide-up">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground font-mono">SIGNAL PREVIEW</span>
                  <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold uppercase rounded-sm">BUY</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-500 font-bold text-2xl">XAUUSD</span>
                    <span className="text-muted-foreground font-mono text-sm">1H Bias</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-secondary/50 border border-border">
                      <div className="text-xs text-muted-foreground mb-1">ENTRY</div>
                      <div className="font-mono text-lg" data-price>2,648.50</div>
                    </div>
                    <div className="p-3 bg-red-500/10 border border-red-500/30">
                      <div className="text-xs text-red-400 mb-1">STOP LOSS</div>
                      <div className="font-mono text-lg text-red-400" data-price>2,642.30</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 bg-green-500/10 border border-green-500/30">
                      <div className="text-xs text-green-400 mb-1">TP1</div>
                      <div className="font-mono text-sm text-green-400" data-price>2,655.00</div>
                    </div>
                    <div className="p-3 bg-green-500/10 border border-green-500/30">
                      <div className="text-xs text-green-400 mb-1">TP2</div>
                      <div className="font-mono text-sm text-green-400" data-price>2,662.80</div>
                    </div>
                    <div className="p-3 bg-green-500/10 border border-green-500/30">
                      <div className="text-xs text-green-400 mb-1">TP3</div>
                      <div className="font-mono text-sm text-green-400" data-price>2,670.50</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instruments Bar */}
      <section className="py-8 border-y border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">Supported:</span>
            {instruments.map((instrument) => (
              <span 
                key={instrument}
                className="font-mono text-lg font-bold"
                data-testid={`instrument-${instrument.toLowerCase()}`}
              >
                {instrument === "XAUUSD" || instrument === "XAGUSD" ? (
                  <span className="text-amber-500">{instrument}</span>
                ) : (
                  instrument
                )}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">HOW IT WORKS</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our deterministic strategy engine follows a strict 6-step process to generate high-probability trade signals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border p-6 hover:border-primary/50 transition-colors duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-secondary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy Steps */}
      <section className="py-24 px-6 bg-card/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black mb-12 text-center">STRATEGY ENGINE STEPS</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Determine Bias", desc: "Analyze structure on 1H/30M for HH→HL (bullish) or LL→LH (bearish)" },
              { step: "02", title: "News Lockout", desc: "Block trades during high-impact economic events" },
              { step: "03", title: "Session Levels", desc: "Calculate Asia, London, NY session highs/lows" },
              { step: "04", title: "Core Setup", desc: "Identify entry at HL/LH midpoint with proper SL" },
              { step: "05", title: "Confirmation", desc: "Validate with VWAP/MVWAP regime and RSI conditions" },
              { step: "06", title: "TP Generation", desc: "Calculate 3 mechanical take-profit targets" }
            ].map((item, index) => (
              <div 
                key={index}
                className="p-6 border border-border bg-card hover:bg-secondary/20 transition-colors"
              >
                <div className="font-mono text-3xl text-muted-foreground mb-4">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOMO Popup Modal */}
      {showBotPromo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="bot-promo-popup-landing">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowBotPromo(false)} />
          <div className="relative w-full max-w-lg bg-[#111] border-2 border-amber-500/70 p-6 animate-in fade-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowBotPromo(false)}
              className="absolute top-3 right-3 text-muted-foreground hover:text-white transition-colors"
              data-testid="close-promo-popup-landing"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="text-orange-400 font-bold text-sm uppercase tracking-wider">Limited Time Offer</span>
            </div>

            <h3 className="text-2xl font-black text-white mb-2">Hybrid Automated MT5 Bot</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Lifetime access to our fully automated trading bot — no recurring fees, ever.
            </p>

            <div className="flex items-center gap-4 mb-4">
              <span className="text-2xl text-muted-foreground line-through font-mono">$699</span>
              <span className="text-4xl font-black text-white font-mono">$299</span>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold border border-green-500/30">
                SAVE $400
              </span>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 p-3 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Timer className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-xs font-bold uppercase">Offer expires in:</span>
              </div>
              <div className="flex items-center gap-2 font-mono text-xl text-white font-bold">
                <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.hours).padStart(2, '0')}</span>
                <span className="text-red-400">:</span>
                <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.minutes).padStart(2, '0')}</span>
                <span className="text-red-400">:</span>
                <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.seconds).padStart(2, '0')}</span>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              {["Lifetime license — one time payment", "Complimentary Set Files (worth $100)", "All 5 instruments supported", "Free updates forever"].map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-sm text-white/80">{f}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => {
                setShowBotPromo(false);
                document.getElementById('hybrid-bot-landing')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider rounded-none"
              data-testid="promo-get-now-btn-landing"
            >
              <Zap className="w-4 h-4 mr-2" /> Get It Now — $299
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-2">One-time payment. No subscription required.</p>
          </div>
        </div>
      )}

      {/* Hybrid MT5 Bot - Featured Package */}
      <section className="py-24 px-6 bg-card/30 border-y border-amber-500/20" id="hybrid-bot-landing">
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden" data-testid="hybrid-bot-package-landing">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5 pointer-events-none" />
            <div className="border-2 border-amber-500/60 bg-card p-8 relative">
              {/* Limited Time Badge */}
              <div className="absolute top-0 right-0 bg-amber-500 text-black px-4 py-1.5 font-bold text-xs uppercase tracking-wider">
                Limited Time Offer
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Left - Product Info */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">Hybrid Automated MT5 Bot</h2>
                      <p className="text-sm text-amber-400 font-medium">Lifetime Access — One Time Purchase</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                    Get the complete Hybrid Automated MT5 Expert Advisor with full strategy implementation. 
                    Trade automatically with our proven algorithm — no recurring fees, lifetime updates included.
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      "Fully automated MT5 Expert Advisor",
                      "Complete hybrid strategy (local + API signals)",
                      "Lifetime license — pay once, use forever",
                      "Complimentary Set Files included (worth $100)",
                      "All supported instruments (XAUUSD, XAGUSD, EURUSD, AUDUSD, USDJPY)",
                      "Free lifetime updates & improvements",
                      "Priority support channel"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="text-sm text-white/90">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right - Pricing & Countdown */}
                <div className="text-center">
                  <div className="bg-secondary/40 border border-border p-6">
                    {/* Countdown Timer */}
                    <div className="mb-4 pb-4 border-b border-border">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Timer className="w-4 h-4 text-red-400" />
                        <span className="text-red-400 text-xs font-bold uppercase tracking-wider">Offer expires in</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 font-mono text-lg text-white font-bold">
                        <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.hours).padStart(2, '0')}</span>
                        <span className="text-red-400">:</span>
                        <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.minutes).padStart(2, '0')}</span>
                        <span className="text-red-400">:</span>
                        <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.seconds).padStart(2, '0')}</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">Actual Price</span>
                      <div className="text-2xl text-muted-foreground line-through font-mono">$699</div>
                    </div>
                    <div className="mb-3">
                      <span className="text-xs text-amber-400 uppercase tracking-wider font-bold">Limited Time</span>
                      <div className="text-5xl font-black text-white font-mono">$299</div>
                    </div>
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold border border-green-500/30">
                        You Save $400!
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-0.5">+ Complimentary Set Files</div>
                    <div className="text-xs text-green-400 font-medium mb-5">(Normally $100 — FREE with this package)</div>
                    
                    <Link to="/register">
                      <Button className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider rounded-none mb-3">
                        <Zap className="w-4 h-4 mr-2" /> Get It Now — $299
                      </Button>
                    </Link>
                    
                    <div className="text-xs text-muted-foreground">
                      One-time payment &middot; No subscription &middot; Lifetime access
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">SIMPLE PRICING</h2>
            <p className="text-muted-foreground">Start with 4 free signal generations, then subscribe for full access including 5M & 15M timeframes.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Plan */}
            <div className="bg-card border border-border p-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold">Monthly</h3>
                <p className="text-sm text-muted-foreground">Flexible month-to-month</p>
              </div>
              <div className="mb-6">
                <div className="text-4xl font-mono font-bold" data-numeric>$30</div>
                <div className="text-muted-foreground">/month</div>
              </div>
              <div className="space-y-3 mb-8">
                {[
                  "Unlimited signals",
                  "All 5 instruments",
                  "All timeframes",
                  "Pay with USDT"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/register">
                <Button 
                  variant="outline"
                  className="w-full rounded-none uppercase tracking-wider font-bold h-12"
                >
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Annual Plan - Best Value */}
            <div className="bg-card border-2 border-green-500 p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-green-500 text-black text-xs font-bold uppercase">
                Best Value - Save 15%
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-bold">Annual</h3>
                <p className="text-sm text-muted-foreground">Best value for serious traders</p>
              </div>
              <div className="mb-2">
                <span className="text-2xl text-muted-foreground line-through mr-2">$360</span>
                <span className="text-4xl font-mono font-bold text-green-400" data-numeric>$306</span>
              </div>
              <div className="text-muted-foreground mb-2">/year</div>
              <div className="inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold mb-6">
                You save $54!
              </div>
              <div className="space-y-3 mb-8">
                {[
                  "Everything in Monthly",
                  "2 months FREE",
                  "Priority support",
                  "Lock in price"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Link to="/register">
                <Button 
                  className="w-full bg-green-500 text-black hover:bg-green-400 rounded-none uppercase tracking-wider font-bold h-12"
                >
                  Get Started - Save $54
                </Button>
              </Link>
            </div>
          </div>

          {/* Free trial note */}
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              🎁 Start with <strong className="text-white">4 free signal generations</strong> - No payment required
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              <span className="font-bold">Aurex AI</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-white transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Aurex AI. All rights reserved.
            </p>
          </div>
          <div className="border-t border-border pt-6">
            <p className="text-xs text-muted-foreground text-center leading-relaxed">
              <span className="font-semibold text-amber-500/80">Disclaimer:</span> Aurex AI provides algorithmic market analysis, not financial advice. Trading decisions are the sole responsibility of the user. Aurex AI does not guarantee profits and is not liable for any losses.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
